// src/hooks/trafficSocketHooks.js - Versión con formateo consistente de unidades y sin advertencias ESLint
import { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { useClientAuth } from '../contexts/ClientAuthContext';

/**
 * Hook personalizado para suscribirse a actualizaciones de tráfico en tiempo real
 * Esta versión asegura consistencia de unidades en todos los componentes
 */
export function useTrafficSocket() {
    const { client, clientToken } = useClientAuth();
    const [realtimeData, setRealtimeData] = useState(null);
    const [trafficHistory, setTrafficHistory] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [error, setError] = useState(null);
    const [usePolling, setUsePolling] = useState(false);

    const socketRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const retryTimerRef = useRef(null);
    const retryCountRef = useRef(0);
    const MAX_RETRIES = 3;

    // Determinar la unidad apropiada según el valor en Kbps
    const determineUnit = useCallback((valueKbps) => {
        return valueKbps >= 1024 ? 'Mbps' : 'Kbps';
    }, []);

    // Formatear un valor en Kbps a la unidad apropiada (Kbps o Mbps)
    const formatRateValue = useCallback((valueKbps) => {
        if (valueKbps >= 1024) {
            return `${(valueKbps / 1024).toFixed(2)} Mbps`;
        }
        return `${valueKbps.toFixed(2)} Kbps`;
    }, []);

    // Extraer valores numéricos para la tasa de datos
    const extractRateValue = useCallback((rateStr) => {
        if (!rateStr) return 0;

        // Para valores numéricos
        if (typeof rateStr === 'number') return rateStr;

        // Normalizar string
        const normalizedStr = String(rateStr).trim();

        // Para valores especiales
        if (normalizedStr === '0' || normalizedStr.toLowerCase() === 'unlimited') return 0;

        // Para cadenas como "10M" o "1.5k"
        const match = normalizedStr.match(/^([\d.]+)([KkMmGg])?$/);
        if (!match) return 0;

        const [, value, unit = ''] = match;
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return 0;

        // Convertir a unidades comunes (Kbps)
        switch (unit.toUpperCase()) {
            case 'K': return numValue;
            case 'M': return numValue * 1024;
            case 'G': return numValue * 1024 * 1024;
            default: return numValue;
        }
    }, []);

    // Procesar datos de tráfico (comunes para socket y polling)
    const processTrafficData = useCallback((data) => {
        if (!data) return;

        // Extraer valores numéricos
        const downloadRateKbps = extractRateValue(data.rateDownload || data.downloadRate || '0');
        const uploadRateKbps = extractRateValue(data.rateUpload || data.uploadRate || '0');

        // Determinar unidades según los valores
        const downloadUnit = determineUnit(downloadRateKbps);
        const uploadUnit = determineUnit(uploadRateKbps);

        // Formatear valores para mostrar
        const downloadFormatted = formatRateValue(downloadRateKbps);
        const uploadFormatted = formatRateValue(uploadRateKbps);

        // Actualizar datos en tiempo real con unidades
        setRealtimeData({
            downloadRate: data.rateDownload || data.downloadRate || '0',
            uploadRate: data.rateUpload || data.uploadRate || '0',
            downloadBytes: data.downloadBytes || 0,
            uploadBytes: data.uploadBytes || 0,
            timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
            // Añadir unidades
            downloadUnit,
            uploadUnit,
            // Añadir valores en Kbps para cálculos
            downloadRateKbps,
            uploadRateKbps,
            // Añadir valores formateados para mostrar
            downloadFormatted,
            uploadFormatted
        });

        // Actualizar historial con nuevos puntos
        const timestamp = data.timestamp ? new Date(data.timestamp) : new Date();

        setTrafficHistory(prev => {
            // Crear nuevo punto con unidades
            const newPoint = {
                time: timestamp.toLocaleTimeString(),
                epoch: timestamp.getTime(),
                // Mantener valores en Kbps para cálculos internos
                downloadRateKbps,
                uploadRateKbps,
                // Guardar unidades para el tooltip
                downloadUnit,
                uploadUnit,
                // Mantener valores originales
                downloadRateOriginal: data.rateDownload || data.downloadRate,
                uploadRateOriginal: data.rateUpload || data.uploadRate,
                // Formatear valores para mostrar
                downloadFormatted,
                uploadFormatted
            };

            // Añadir el nuevo punto y mantener solo los últimos 60
            const newHistory = [...prev, newPoint].slice(-60);
            return newHistory;
        });

        setLastUpdated(new Date());
    }, [determineUnit, extractRateValue, formatRateValue]);

    // Obtener datos de tráfico mediante REST API (para polling)
    const fetchTrafficData = useCallback(async () => {
        if (!client || !clientToken) return;

        try {
            const response = await fetch('/api/client/traffic/current', {
                headers: {
                    'Authorization': `Bearer ${clientToken}`,
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }

            const data = await response.json();
            console.log("Datos de tráfico recibidos via REST API:", data);

            // Procesar datos de current
            if (data.current) {
                // Transformar formato para que coincida con los datos de socket
                const formattedData = {
                    clientId: client.id,
                    rateDownload: data.current.download || '0',
                    rateUpload: data.current.upload || '0',
                    downloadBytes: data.total?.download || 0,
                    uploadBytes: data.total?.upload || 0,
                    timestamp: new Date()
                };

                processTrafficData(formattedData);
            }

            // Procesar historial
            if (data.history && Array.isArray(data.history)) {
                setTrafficHistory(data.history.map(point => {
                    // Extraer valores numéricos y mantener unidades originales
                    const downloadRateKbps = extractRateValue(point.download || '0');
                    const uploadRateKbps = extractRateValue(point.upload || '0');

                    // Determinar unidades según los valores
                    const downloadUnit = determineUnit(downloadRateKbps);
                    const uploadUnit = determineUnit(uploadRateKbps);

                    return {
                        time: point.time || new Date().toLocaleTimeString(),
                        epoch: point.epoch_time || Date.now() / 1000,
                        // Mantener valores en Kbps para cálculos internos
                        downloadRateKbps: downloadRateKbps,
                        uploadRateKbps: uploadRateKbps,
                        // Guardar unidades para el tooltip
                        downloadUnit,
                        uploadUnit,
                        // Mantener valores originales
                        downloadRateOriginal: point.download,
                        uploadRateOriginal: point.upload,
                        // Valores formateados como se muestran en el panel
                        downloadFormatted: formatRateValue(downloadRateKbps),
                        uploadFormatted: formatRateValue(uploadRateKbps)
                    };
                }));
            }
        } catch (error) {
            console.error("Error al obtener datos de tráfico via REST:", error);
            setError(`Error al obtener datos: ${error.message}`);
        }
    }, [client, clientToken, determineUnit, extractRateValue, formatRateValue, processTrafficData]);

    // Limpiar temporizadores y recursos
    const cleanup = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }

        if (socketRef.current) {
            try {
                socketRef.current.disconnect();
            } catch (e) {
                console.warn("Error al desconectar socket:", e);
            }
            socketRef.current = null;
        }
    }, []);

    // Forzar reconexión o refresco de datos
    const refreshData = useCallback(() => {
        if (usePolling) {
            // En modo polling, simplemente obtener nuevos datos
            fetchTrafficData();
        } else {
            // En modo socket, intentar reconectar
            cleanup();
            retryCountRef.current = 0; // Resetear contador
            // El socket se recreará en el siguiente ciclo de efecto
        }
    }, [usePolling, fetchTrafficData, cleanup]);

    // Inicializar socket o polling según corresponda
    useEffect(() => {
        if (!client || !clientToken) {
            console.log("Sin cliente o token, no se inicia conexión");
            cleanup();
            return;
        }

        // Si estamos en modo polling, iniciar intervalo
        if (usePolling) {
            console.log("Iniciando modo polling para datos de tráfico");
            fetchTrafficData(); // Cargar datos inmediatamente

            // Iniciar intervalo para actualizar cada 5 segundos
            pollingIntervalRef.current = setInterval(() => {
                fetchTrafficData();
            }, 5000);

            return cleanup; // Limpiar al desmontar
        }

        // Si ya existe una conexión socket, no hacer nada
        if (socketRef.current && socketRef.current.connected) {
            console.log("Socket ya conectado, no se crea uno nuevo");
            return;
        }

        console.log("Iniciando conexión socket para tráfico");

        try {
            // Limpiar cualquier conexión existente
            cleanup();

            // Asegurarse que el token no tenga prefijo "Bearer "
            const tokenToUse = clientToken.startsWith('Bearer ')
                ? clientToken.slice(7)
                : clientToken;

            // Crear nueva conexión
            socketRef.current = io(process.env.REACT_APP_SOCKET_URL || '', {
                auth: {
                    token: tokenToUse,
                    clientId: client.id,
                    type: 'client'
                },
                transports: ['polling', 'websocket'], // Intentar polling primero para mayor éxito
                reconnection: true,
                reconnectionAttempts: 2,
                reconnectionDelay: 1000,
                timeout: 10000
            });

            // Configurar manejadores de eventos
            socketRef.current.on('connect', () => {
                console.log("Socket conectado exitosamente:", socketRef.current.id);
                setIsConnected(true);
                setError(null);
                retryCountRef.current = 0;

                // Suscribirse a actualizaciones de tráfico
                socketRef.current.emit('subscribe:traffic');
            });

            socketRef.current.on('connect_error', (err) => {
                console.error("Error de conexión socket:", err.message);
                setIsConnected(false);
                setError(`Error de conexión: ${err.message}`);

                // Si ya intentamos demasiadas veces, cambiar a polling
                if (retryCountRef.current >= MAX_RETRIES) {
                    console.log(`Máximo de reintentos (${MAX_RETRIES}) alcanzado, cambiando a modo polling`);
                    setUsePolling(true);
                    setError('Usando actualizaciones periódicas como alternativa');
                } else {
                    // Incrementar contador de reintentos
                    retryCountRef.current++;

                    // Programar reintento
                    const delay = Math.min(1000 * Math.pow(1.5, retryCountRef.current), 5000);
                    console.log(`Reintentando en ${delay / 1000}s (intento ${retryCountRef.current}/${MAX_RETRIES})`);

                    retryTimerRef.current = setTimeout(() => {
                        // Desconectar y recrear socket
                        if (socketRef.current) {
                            socketRef.current.disconnect();
                            socketRef.current = null;
                        }
                        // La recreación ocurrirá en el siguiente ciclo de efecto
                    }, delay);
                }
            });

            socketRef.current.on('disconnect', (reason) => {
                console.log("Socket desconectado:", reason);
                setIsConnected(false);

                // Si es una desconexión forzada por el servidor, considerar cambiar a polling
                if (reason === 'io server disconnect') {
                    console.log("Desconexión forzada por el servidor, considerando cambio a polling");
                    if (retryCountRef.current >= MAX_RETRIES - 1) {
                        setUsePolling(true);
                    }
                }
            });

            // Evento de autenticación exitosa
            socketRef.current.on('auth_success', (data) => {
                console.log("Autenticación de socket exitosa:", data);
            });

            // Suscripción confirmada
            socketRef.current.on('subscribed', (data) => {
                console.log("Suscripción a datos confirmada:", data);
            });

            // Escuchar actualizaciones de tráfico
            socketRef.current.on('traffic:update', (data) => {
                console.log("Datos de tráfico recibidos via socket:", data);
                processTrafficData(data);
            });

            // Manejar errores
            socketRef.current.on('error', (error) => {
                console.error("Error de socket:", error);
                setError(`Error: ${error}`);
            });
        } catch (error) {
            console.error("Error al inicializar socket:", error);
            setError(`Error al inicializar conexión: ${error.message}`);
            setUsePolling(true); // Cambiar a polling como fallback
        }

        // Limpiar recursos al desmontar
        return cleanup;
    }, [client, clientToken, usePolling, cleanup, fetchTrafficData, processTrafficData]);

    // Formatear velocidad para mostrar con unidades correctas
    const formatRate = useCallback((rate) => {
        if (!rate) return '0 Kbps';

        // Si es una cadena como "10M" o "1.5k", procesarla
        if (typeof rate === 'string') {
            const match = rate.match(/^([\d.]+)([KkMmGg])?$/);
            if (match) {
                const [, value, unit = ''] = match;
                const unitUpper = unit.toUpperCase();
                return `${value} ${unitUpper || 'K'}bps`;
            }
            return rate;
        }

        // Si es un número (en Kbps), convertir a unidad apropiada
        const valueKbps = parseFloat(rate);
        if (isNaN(valueKbps)) return '0 Kbps';

        if (valueKbps >= 1024) {
            return `${(valueKbps / 1024).toFixed(2)} Mbps`;
        }
        return `${valueKbps.toFixed(2)} Kbps`;
    }, []);

    // Formatear bytes para mostrar
    const formatBytes = useCallback((bytes) => {
        if (!bytes || isNaN(bytes)) return '0 B';

        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    }, []);

    // Preparar datos para gráficos
    const prepareChartData = useCallback(() => {
        return trafficHistory.map(item => ({
            time: item.time,
            epoch: item.epoch,
            // Mantener datos en Kbps para escala correcta
            downloadRate: item.downloadRateKbps || 0,
            uploadRate: item.uploadRateKbps || 0,
            // Incluir unidades para el tooltip
            downloadUnit: item.downloadUnit || 'Kbps',
            uploadUnit: item.uploadUnit || 'Kbps',
            // Valores en Kbps para cálculos
            downloadRateKbps: item.downloadRateKbps || 0,
            uploadRateKbps: item.uploadRateKbps || 0,
            // Valores formateados para mostrar
            downloadFormatted: item.downloadFormatted || formatRateValue(item.downloadRateKbps || 0),
            uploadFormatted: item.uploadFormatted || formatRateValue(item.uploadRateKbps || 0)
        }));
    }, [trafficHistory, formatRateValue]);

    return {
        realtimeData,
        trafficHistory,
        isConnected,
        lastUpdated,
        error,
        usePolling,
        formatRate,
        formatBytes,
        extractRateValue,
        chartData: prepareChartData(),
        refreshData,
        determineUnit,
        formatRateValue
    };
}

export default useTrafficSocket;